/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'

import {
    SimpleGrid,
    Button,
    Box,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {
    ADD_FILTER,
    REMOVE_FILTER
} from '@salesforce/retail-react-app/app/pages/product-list/partials/refinements-utils'
import {REFINEMENT_LEGEND} from '../../product-list'

/**
 * SizeRefinements is a React functional component that renders a set of size filter options
 * for product listings. It allows users to refine their search results based on available sizes.
 * The component typically displays a list of selectable size options (e.g., S, M, L, XL) and
 * manages the selection state, updating the product list accordingly when a size is selected or deselected.
 *
 * @component
 * @example
 * <SizeRefinements
 *   sizes={['S', 'M', 'L', 'XL']}
 *   selectedSizes={['M']}
 *   onChange={handleSizeChange}
 * />
 *
 * @param {Object} props - The component props.
 * @param {Array<string>} props.sizes - Array of available size options.
 * @param {Array<string>} props.selectedSizes - Array of currently selected sizes.
 * @param {Function} props.onChange - Callback function invoked when the selection changes.
 *
 * @returns {JSX.Element} A set of size refinement options for filtering products.
 */
const SizeRefinements = ({filter, selectedFilters, onSelect, onDeselect}) => {
    const {formatMessage} = useIntl()

    const styles = useMultiStyleConfig('SwatchGroup', {
        variant: 'sizeRefinement',
        disabled: false
    })

    return (
        filter.values?.length > 0 && (
            <Box as="fieldset">
                {/* ADA legend */}
                <legend className="sr-only">
                    {formatMessage(REFINEMENT_LEGEND, {label: filter.label})}
                </legend>

                <SimpleGrid
                    as="ul"
                    listStyleType={'none'}
                    width="full"
                    templateColumns="repeat(5, 1fr)"
                    spacingX={0}
                    spacingY={10}
                >
                    {filter.values?.map((value) => {
                        // Note the loose comparison, for "string == number" checks.
                        const isSelected = selectedFilters.some(
                            (filterValue) => filterValue == value.value
                        )
                        // Don't display refinements with no results, unless we got there by selecting too
                        // many refinements
                        if (value.hitCount === 0 && !isSelected) return

                        return (
                            <Box as="li" key={`size-button-${value.value}`}>
                                <Button
                                    {...styles.swatch}
                                    role="checkbox"
                                    variant="outline"
                                    borderColor={isSelected ? 'fullBlack' : 'gray'}
                                    backgroundColor={isSelected ? 'fullBlack' : 'transparent'}
                                    color={isSelected ? 'white' : 'darkGray'}
                                    zIndex={isSelected ? 1 : 0}
                                    onClick={() => {
                                        if (isSelected) {
                                            onDeselect(value.value)
                                        } else {
                                            onSelect(value.value)
                                        }
                                    }}
                                    aria-checked={isSelected}
                                    aria-label={formatMessage(
                                        isSelected ? REMOVE_FILTER : ADD_FILTER,
                                        value
                                    )}
                                >
                                    {value.label}
                                </Button>
                            </Box>
                        )
                    })}
                </SimpleGrid>
            </Box>
        )
    )
}

SizeRefinements.propTypes = {
    filter: PropTypes.object,
    selectedFilters: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
    onSelect: PropTypes.func,
    onDeselect: PropTypes.func
}

// Memoized SizeRefinements to prevent unnecessary re-renders.
// Use case: accordion open / close with SizeRefinements
export default React.memo(SizeRefinements)
